/* Ott-common */
import {
  ShowEntity,
  ChannelEntity,
  ContentEntity,
  ScheduleEntity,
  CarouselType,
} from '@dtvgo/backend-service/normalize';

/* Utils */
import { CollectionText, SOURCE_CAROUSEL_ID, UHD_WIDTH_BASE } from './constants';
import { getWebAction } from './metaDataUtils';
import {
  readCarouselField,
  readCarouselRowType,
  isToastBannerRowType,
  isHomeBannerRowType,
} from './landerCommunicationHelpers';

/**
 * @enum {string}
 * @readonly
 */
export const CardType = {
  FEATURED: 'FEATURED',
  VOD: 'VOD',
  LIVE: 'LIVE',
  CHANNEL: 'CHANNEL',
  COLLECTION: 'COLLECTION',
  SPORT_SECTION: 'SPORT_SECTION',
  PLAY_OFFS: 'PLAY_OFFS',
  GROUP_STANDING: 'GROUP_STANDING',
  IMAGE_BANNER: 'IMAGE_BANNER',
  HOME_COMMUNICATION_BANNER: 'HOME_COMMUNICATION_BANNER',
  HOME_COMMUNICATION_BANNER_COMPACT: 'HOME_COMMUNICATION_BANNER_COMPACT',
  TOAST_COMMUNICATION_BANNER: 'TOAST_COMMUNICATION_BANNER',
  TOAST_FEATURED_COMMUNICATION_STACK: 'TOAST_FEATURED_COMMUNICATION_STACK',
  UPSELL_BANNER: 'UPSELL_BANNER',
  CONTINUE_WATCHING: 'CONTINUE_WATCHING',
  UNKNOWN: 'UNKNOWN',
  EMPTY: 'EMPTY',
};

export const CardTypeByCarouselType = {
  [CarouselType.CHANNELS]: CardType.CHANNEL,
  [CarouselType.COLLECTIONS]: CardType.COLLECTION,
  [CarouselType.CONTINUE_WATCHING]: CardType.CONTINUE_WATCHING,
  [CarouselType.HERO_NOW_NEXT]: CardType.FEATURED,
  [CarouselType.HIGHLIGHTS]: CardType.FEATURED,
  [CarouselType.IMAGE_BANNER]: CardType.IMAGE_BANNER,
  [CarouselType.LANDSCAPE]: CardType.VOD,
  [CarouselType.LIVE_AUTOMATIC]: CardType.LIVE,
  [CarouselType.LIVE_EDITORIAL]: CardType.LIVE,
  [CarouselType.LIVE_HERO_AUTOMATIC]: CardType.FEATURED,
  [CarouselType.LIVE_HERO_EDITORIAL]: CardType.FEATURED,
  [CarouselType.MOST_WATCHED]: CardType.CHANNEL,
  [CarouselType.MY_LIST]: CardType.VOD,
  [CarouselType.NOW_NEXT]: CardType.LIVE,
  [CarouselType.PORTRAIT]: CardType.VOD,
  [CarouselType.RECENTLY_WATCHED]: CardType.CHANNEL,
  [CarouselType.TA_HERO]: CardType.FEATURED,
  [CarouselType.TA_LIVE]: CardType.LIVE,
  [CarouselType.TA_VOD]: CardType.VOD,
  [CarouselType.PLAY_OFFS]: CardType.PLAY_OFFS,
  [CarouselType.SPORT_STANDING_GROUP]: CardType.GROUP_STANDING,
  [CarouselType.SPORT_STANDING_GROUP_DYNAMIC]: CardType.GROUP_STANDING,
  [CarouselType.SPORT_STANDING_PLAYOFF]: CardType.PLAY_OFFS,
  [CarouselType.SPORT_LIVE_DYNAMIC]: CardType.LIVE,
  [CarouselType.SPORT_VOD_BANNER_DYNAMIC]: CardType.SPORT_SECTION,
  [CarouselType.SPORT_VOD_PORTRAIT_DYNAMIC]: CardType.VOD,
};

export const CardWidth = {
  [CardType.FEATURED]: 1498,
  [CardType.VOD]: 280,
  [CardType.LIVE]: 446,
  [CardType.CHANNEL]: 446,
  [CardType.COLLECTION]: 356,
  [CardType.SPORT_SECTION]: 280,
  [CardType.PLAY_OFFS]: 1543,
  [CardType.GROUP_STANDING]: 566,
  [CardType.IMAGE_BANNER]: 1520,
  [CardType.HOME_COMMUNICATION_BANNER]: 1520,
  [CardType.HOME_COMMUNICATION_BANNER_COMPACT]: 1520,
  [CardType.TOAST_COMMUNICATION_BANNER]: 1520,
  [CardType.TOAST_FEATURED_COMMUNICATION_STACK]: 1520,
  [CardType.CONTINUE_WATCHING]: 356,
};

const LANDER_HERO_STACK_TOP_PAD_UHD = 40;
const LANDER_TOAST_STRIP_HEIGHT_UHD = 110;
const LANDER_TOAST_HOME_GAP_UHD = 48;
const LANDER_HOME_BANNER_SLOT_UHD = 520;
const LANDER_HOME_BANNER_ROW_MARGIN_UHD = 30 + 30 + 4;

export const MaxCardHeight = {
  [CardType.FEATURED]: 702 + 40,
  [CardType.VOD]: 420 + 40 + 103,
  [CardType.LIVE]: 440 + 20,
  [CardType.CHANNEL]: 172 + 20,
  [CardType.COLLECTION]: 356 + 20,
  [CardType.SPORT_SECTION]: 356 + 20,
  [CardType.IMAGE_BANNER]: 568 + 20,
  [CardType.HOME_COMMUNICATION_BANNER]:
    LANDER_HOME_BANNER_SLOT_UHD + 20 + LANDER_HERO_STACK_TOP_PAD_UHD
    + LANDER_TOAST_STRIP_HEIGHT_UHD + LANDER_TOAST_HOME_GAP_UHD + 20,
  [CardType.HOME_COMMUNICATION_BANNER_COMPACT]:
    LANDER_HOME_BANNER_SLOT_UHD + LANDER_HOME_BANNER_ROW_MARGIN_UHD + 20,
  [CardType.TOAST_COMMUNICATION_BANNER]: 140 + 20,
  [CardType.TOAST_FEATURED_COMMUNICATION_STACK]:
    140 + 20 + LANDER_HERO_STACK_TOP_PAD_UHD
    + LANDER_TOAST_STRIP_HEIGHT_UHD + LANDER_TOAST_HOME_GAP_UHD,
  [CardType.PLAY_OFFS]: 754 + 20,
  [CardType.GROUP_STANDING]: 432 + 40,
  [CardType.CONTINUE_WATCHING]: 200 + 20 + 103,
};

export const CarouselHeight = {
  [CardType.FEATURED]: 812,
  [CardType.VOD]: 682,
  [CardType.LIVE]: 578,
  [CardType.CHANNEL]: 312,
  [CardType.COLLECTION]: 494,
  [CardType.SPORT_SECTION]: 494,
  [CardType.IMAGE_BANNER]: 648,
  [CardType.HOME_COMMUNICATION_BANNER]:
    LANDER_HERO_STACK_TOP_PAD_UHD
    + LANDER_TOAST_STRIP_HEIGHT_UHD
    + LANDER_TOAST_HOME_GAP_UHD
    + LANDER_HOME_BANNER_SLOT_UHD,
  [CardType.HOME_COMMUNICATION_BANNER_COMPACT]:
    LANDER_HOME_BANNER_SLOT_UHD + LANDER_HOME_BANNER_ROW_MARGIN_UHD,
  [CardType.TOAST_COMMUNICATION_BANNER]: 150,
  [CardType.TOAST_FEATURED_COMMUNICATION_STACK]:
    LANDER_HERO_STACK_TOP_PAD_UHD
    + LANDER_TOAST_STRIP_HEIGHT_UHD
    + LANDER_TOAST_HOME_GAP_UHD
    + 812,
  [CardType.UPSELL_BANNER]: 344,
  [CardType.PLAY_OFFS]: 892,
  [CardType.GROUP_STANDING]: 590,
  [CardType.CONTINUE_WATCHING]: 442,
  [CardType.UNKNOWN]: 578,
  [CardType.EMPTY]: 0,
};

export const CARDS_TO_SHOW_IN_SWIMLANE = {
  [CardType.FEATURED]: 1,
  [CardType.VOD]: 5,
  [CardType.LIVE]: 3,
  [CardType.CHANNEL]: 3,
  [CardType.COLLECTION]: 4,
  [CardType.SPORT_SECTION]: 4,
  [CardType.PLAY_OFFS]: 1,
  [CardType.GROUP_STANDING]: 3,
  [CardType.IMAGE_BANNER]: 1,
  [CardType.HOME_COMMUNICATION_BANNER]: 1,
  [CardType.HOME_COMMUNICATION_BANNER_COMPACT]: 1,
  [CardType.TOAST_COMMUNICATION_BANNER]: 1,
  [CardType.TOAST_FEATURED_COMMUNICATION_STACK]: 1,
  [CardType.CONTINUE_WATCHING]: 4,
};

export const CardTypeByEntity = {
  [ContentEntity.name]: CardType.VOD,
  [ShowEntity.name]: CardType.VOD,
  [ScheduleEntity.name]: CardType.LIVE,
  [ChannelEntity.name]: CardType.CHANNEL,
};

// It should be removed from the code when pages v4 is the final version.
export const BannerType = {
  UPSELL_BANNER: 'upsell-banner',
};

export function isEmptyCarousel(carousel) {
  return !carousel?.collections?.size
    && !carousel?.contents?.size
    && !carousel?.sport?.standing?.groups
    && !carousel?.sport?.standing?.playoffStages;
}

export function getInitialCardType(carouselInfo, noFeatured = false) {
  if (!carouselInfo) {
    return CardType.EMPTY;
  }
  const sourceCarouselId = readCarouselField(carouselInfo, 'sourceCarouselId');
  const elementId = readCarouselField(carouselInfo, 'elementId');
  const type = readCarouselRowType(carouselInfo);

  if ((sourceCarouselId || elementId) === SOURCE_CAROUSEL_ID.CONTINUE_WATCHING) {
    return CardType.CONTINUE_WATCHING;
  }

  if ((sourceCarouselId || elementId) === SOURCE_CAROUSEL_ID.RECENTLY_WATCHED) {
    return CardType.CHANNEL;
  }

  if (isHomeBannerRowType(type)) {
    return CardType.HOME_COMMUNICATION_BANNER_COMPACT;
  }

  if (isToastBannerRowType(type)) {
    return CardType.TOAST_COMMUNICATION_BANNER;
  }

  const cardType = CardTypeByCarouselType[type];
  if (cardType === CardType.FEATURED && noFeatured) return CardType.UNKNOWN;

  return cardType || CardType.EMPTY;
}

export function getCardType(carousel, noFeatured = false) {
  if (!carousel) return CardType.UNKNOWN;

  if (carousel && isEmptyCarousel(carousel)) return CardType.EMPTY;

  if (getWebAction(carousel?.collections?.get(0))?.text === CollectionText.SPORT_SECTION) {
    return CardType.SPORT_SECTION;
  }

  const initialCardType = getInitialCardType(carousel);
  if (initialCardType === CardType.FEATURED && noFeatured) {
    const { contents: [{ schema } = {}] = [] } = carousel || {};
    return CardTypeByEntity[schema];
  }

  return initialCardType;
}

export function getCarouselHeight(cardType) {
  return CarouselHeight[cardType];
}

export function sizeByViewport(size, isRound = true) {
  const { innerWidth } = window;
  const sizeConverted = (size * innerWidth) / UHD_WIDTH_BASE;
  return isRound ? Math.ceil(sizeConverted) : sizeConverted;
}
